import { useCallback, useMemo, useState } from 'react';
import { useFamilyMembers } from '../../hooks/useFamilyMembers';
import { useProfile } from '../../contexts/ProfileContext';
import { generateEntityId } from '../../utils/entityId';
import { CHILD_RELATIONS } from '../../config/relationConstants';

/**
 * The children a søknad can be for, and which child a draft belongs to.
 *
 * Subject tiers (PR: person-picker):
 *  - TIER 1: the onboarded child (Profile.child). Generation reads the profile.
 *  - TIER 2: a chat-added child (subjectIsOnboardedChild:false); the wizard
 *    asks the parent to confirm the facts on its own step.
 */

/**
 * The onboarded child's entity id, mirroring how onboarding wrote it
 * (wizardMemoryTemplates): alias → generateEntityId(alias), else
 * `child_<birthYear>`. Null if neither is available.
 */
function onboardedChildEntityId(child) {
  if (!child) return null;
  if (child.alias) return generateEntityId(child.alias);
  if (child.birthYear) return `child_${child.birthYear}`;
  return null;
}

/**
 * Is a family-member entity the onboarded child? By entityId (deterministic,
 * diacritic-safe), else by name for an entity whose id doesn't match.
 */
function isOnboardedChild(entity, child) {
  if (!child) return false;
  const childId = onboardedChildEntityId(child);
  if (childId && entity.entityId && entity.entityId === childId) return true;
  if (child.alias && entity.name &&
      generateEntityId(entity.name) === generateEntityId(child.alias)) {
    return true;
  }
  return false;
}

const normalizeRow = (text) => String(text || '').trim().replace(/\.$/, '').replace(/\s+/g, ' ').toLowerCase();

/**
 * Did the parent register this child as a child (Konto or "Legg til barn")?
 * AddFamilyMemberModal writes a "{navn} er {relasjon}" row; chat extraction
 * doesn't. Matched on the text (case, spacing and a final period aside), not
 * the subcategory: a save that updated an existing row of the child leaves
 * that row's subcategory.
 */
function isRegisteredChild(entity) {
  const rows = new Set(CHILD_RELATIONS.map((relation) => normalizeRow(`${entity.name} er ${relation}`)));
  return (entity.memories || []).some((memory) => rows.has(normalizeRow(memory.content)));
}

/**
 * The child a draft (or subject) is for; there is one draft per child per
 * type, on this key. A draft not explicitly about another person is the
 * onboarded child's, including drafts saved without a subject. Mirrors
 * childKey in coord-backend/src/routes/soknad.js.
 */
export function childKey({ subjectIsOnboardedChild, subjectEntityId, subjectName }) {
  if (subjectIsOnboardedChild !== false) return 'onboarded';
  if (subjectEntityId) return `id:${subjectEntityId}`;
  return `name:${String(subjectName || '').toLowerCase()}`;
}

// A draft's subject, as POST /drafts takes it (e.g. to restart for the same child).
export const draftSubject = (draft) => ({
  subjectEntityId: draft.subjectEntityId ?? null,
  subjectName: draft.subjectName ?? null,
  subjectIsOnboardedChild: draft.subjectIsOnboardedChild !== false,
});

/**
 * useSoknadChildren — the children the parent can start a søknad for.
 *
 * Only children the parent registered: the onboarded child, and family
 * members added as sønn/datter/søster/bror on Konto or with "Legg til barn"
 * (isRegisteredChild). People found in chat are not listed; a draft already
 * made for one keeps its row on the overview. The onboarded child is always
 * kept, whatever its type, and comes first; it is taken from the profile when
 * it has no family-member entry.
 *
 * childAdded(entityId, name) is for a child just saved on the overview
 * ("Legg til barn"): it re-fetches the family list, retrying until the new
 * entity is returned (useFamilyMembers' refresh), and lists the child
 * meanwhile. Azure AI Search makes a new memory searchable a moment after the
 * write, and the refresh gives up after ~3 s; the child stays listed until
 * the family list returns it.
 *
 * error is true when the family list failed to load (useFamilyMembers'
 * loadError): children would then hold only the onboarded child, so callers
 * show an error instead of the list; retry() re-fetches the family list.
 *
 * Returns { children: [{ key, name, subject }], loading, error, retry, childAdded }.
 */
export function useSoknadChildren() {
  const { familyMembers, loading: familyLoading, loadError, refresh } = useFamilyMembers();
  const profileCtx = useProfile();
  const child = profileCtx?.profile?.child || null;
  const [added, setAdded] = useState([]);

  const children = useMemo(() => {
    const byKey = new Map();
    (familyMembers || []).forEach((entity) => {
      const subject = {
        subjectEntityId: entity.entityId || null,
        subjectName: entity.name || null,
        subjectIsOnboardedChild: isOnboardedChild(entity, child),
      };
      if (!subject.subjectIsOnboardedChild && !isRegisteredChild(entity)) return;
      const key = childKey(subject);
      if (!byKey.has(key)) byKey.set(key, { key, name: entity.name, subject });
    });
    // Added on the overview, not returned by the family list yet.
    added.forEach(({ entityId, name }) => {
      const subject = { subjectEntityId: entityId, subjectName: name, subjectIsOnboardedChild: false };
      const key = childKey(subject);
      if (!byKey.has(key)) byKey.set(key, { key, name, subject });
    });

    const onboarded = byKey.get('onboarded') || ((child || byKey.size === 0) ? {
      key: 'onboarded',
      name: child?.alias || 'Barnet ditt',
      subject: {
        subjectEntityId: onboardedChildEntityId(child),
        subjectName: child?.alias || null,
        subjectIsOnboardedChild: true,
      },
    } : null);
    byKey.delete('onboarded');

    const others = [...byKey.values()].sort((a, b) => a.name.localeCompare(b.name, 'nb'));
    return onboarded ? [onboarded, ...others] : others;
  }, [familyMembers, child, added]);

  const childAdded = useCallback((entityId, name) => {
    setAdded((prev) => [...prev, { entityId, name }]);
    refresh(entityId);
  }, [refresh]);

  const retry = useCallback(() => refresh(), [refresh]);

  return {
    children,
    loading: familyLoading || !profileCtx?.loaded,
    error: loadError,
    retry,
    childAdded,
  };
}
